import { useState, type ReactNode } from "react";
import { useAnchored } from "../lib/anchored";

/**
 * A small circled "i" — the quiet home for the one thing a screen genuinely
 * needs to say but should not wear on its face.
 *
 * Jonathan, 2026-10-08: the reports have "a lot of words ... all these extra
 * words and explanations are not necessary and they just confuse the eye. If
 * you feel like an explanation is needed, you should add a small info icon and
 * put the text over there." So the prose that used to sit in a band across the
 * top of a report, or as a paragraph under a heading, lives in here instead:
 * present for the reader who wants it, out of the eye of the reader who does
 * not. This is [[feedback-too-many-words]] and [[feedback-ui-cards-not-tables]]
 * ("no explanatory prose on screen") given a single reusable control.
 *
 * It is the same anchored popover as `Menu` — `position: fixed`, shadowed,
 * closes on outside click and Escape, returns focus to the trigger — so it is
 * never cropped by a scroll container and never moves the page. A real caveat
 * (not decoration) still belongs on the face of the page; this is for the
 * explanation that is true, worth having, and not worth a line of everyone's
 * attention.
 */
export default function InfoDot({
  label = "What this is",
  children,
}: {
  /** The accessible name of the button, and the dialog's label. */
  label?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { anchor, panel, style } = useAnchored<HTMLButtonElement>(
    open,
    () => setOpen(false),
    "right",
  );

  return (
    <span className="info-dot no-print">
      <button
        ref={anchor}
        type="button"
        className="info-dot-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((was) => !was)}
      >
        {/* A drawn "i", centred in its viewBox — a dot and a stem both on x=8
            (Jonathan, 2026-10-25: the text glyph "i" has uneven side bearings and
            never sat centred; italic pushed it right, de-italic left). This is
            the one info mark the whole app uses, so it is right everywhere. */}
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="8" cy="4" r="1.2" />
          <rect x="6.8" y="6.6" width="2.4" height="6.4" rx="1.2" />
        </svg>
      </button>
      {open ? (
        <div
          ref={panel}
          className="menu-pop info-pop"
          role="dialog"
          aria-label={label}
          style={style}
        >
          {children}
        </div>
      ) : (
        // Closed, the text is still in the page for a screen reader -- it is
        // hidden from the eye, not removed. (It is also what lets a test check
        // that a screen still says WHY, now that the why sits behind the dot.)
        <span className="visually-hidden">{children}</span>
      )}
    </span>
  );
}
